import React from 'react';
import styles from './MInfoCard.module.css';
import MRadio from './MRadio';
import MOptionsMenu from './MOptionsMenu';
import MBadge from './MBadge';
import MWrap from '@/components/ui/atoms/MWrap';
import { IconCheck, IconAlertTriangle, IconDotsVertical } from '@tabler/icons-react';

export type MInfoCardVariant = 'radio' | 'checkbox' | 'actionsMenu' | 'labels' | 'custom';

export interface ListItemAction {
  id: string;
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
  className?: string;
  isVisible?: boolean;
}

export type MInfoCardProps = {
  primaryLabel: string;
  secondaryLabel?: string;
  tertiaryLabel?: string;
  highlightedLabel?: string;
  warningLabel?: string;
  emphasizedLabel?: string;
  showEmphasizedTertiary?: boolean;
  leftIcon?: React.ReactNode;
  variant?: MInfoCardVariant;
  showCTA?: boolean;
  ctaLabel?: string;
  onAction?: () => void;
  checked?: boolean;
  onCheck?: (checked: boolean) => void;
  selected?: boolean;
  onSelect?: (checked: boolean) => void;
  actions?: ListItemAction[];
  showOptionsMenu?: boolean;
  optionsMenuDisabled?: boolean;
  onOptionsClick?: () => void;
  onPrimaryClick?: () => void;
  primaryClickDisabled?: boolean;
  labelText?: string;
  labelColor?: 'grey' | 'red' | 'orange' | 'yellow' | 'green';
  onLabelClick?: () => void;
  rightActionComponent?: React.ReactNode;
  wrapLabel?: boolean;
  centeredRightAction?: boolean;
  onClick?: () => void;
  isAllergy?: boolean;
};

const MInfoCard: React.FC<MInfoCardProps> = (props) => {
  const {
    primaryLabel,
    secondaryLabel,
    tertiaryLabel,
    highlightedLabel,
    warningLabel,
    emphasizedLabel,
    showEmphasizedTertiary,
    leftIcon,
    variant,
    showCTA,
    ctaLabel,
    onAction,
    checked,
    onCheck,
    selected,
    onSelect,
    actions,
    showOptionsMenu,
    optionsMenuDisabled,
    onOptionsClick,
    onPrimaryClick,
    primaryClickDisabled,
    labelText,
    labelColor,
    onLabelClick,
    rightActionComponent,
    wrapLabel,
    centeredRightAction,
    isAllergy,
  } = props;

  // Convert actions to DropdownOption[] for MOptionsMenu if present
  let dropdownOptions;
  if (actions && actions.length > 0) {
    dropdownOptions = actions.filter(a => a.isVisible !== false).map(action => ({
      id: action.id,
      label: action.label,
      icon: action.icon || null,
      onClick: action.onClick,
      className: action.className,
      isVisible: action.isVisible,
      delete: (action as any).delete,
    }));
  }

  return (
    <div className={`${styles.infoCard} ${isAllergy ? styles.allergyCard : ''}`} onClick={props.onClick} style={{ cursor: props.onClick ? 'pointer' : undefined }}>
      {leftIcon && <div className={styles.leftIcon}>{leftIcon}</div>}
      <div className={styles.labels}>
        <div className={styles.topRow}>
          {onPrimaryClick ? (
            <button
              type="button"
              className={styles.primaryBtn}
              onClick={(e) => {
                e.stopPropagation();
                if (primaryClickDisabled) return;
                onPrimaryClick();
              }}
              disabled={!!primaryClickDisabled}
            >
              {wrapLabel ? <MWrap text={primaryLabel} maxChars={20} /> : primaryLabel}
            </button>
          ) : (
            <div className={styles.primaryText}>
              {wrapLabel ? <MWrap text={primaryLabel} maxChars={20} /> : primaryLabel}
            </div>
          )}
          {(labelText || variant === 'actionsMenu' || actions || showOptionsMenu) && (
            <div className={styles.topRight}>
              {labelText && labelColor && (
                <MBadge variant={labelColor} onClick={onLabelClick}>{labelText}</MBadge>
              )}
              {onOptionsClick ? (
                <button
                  type="button"
                  className={styles.optionsBtn}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (optionsMenuDisabled) return;
                    onOptionsClick();
                  }}
                  aria-label="Options"
                  disabled={!!optionsMenuDisabled}
                >
                  <IconDotsVertical size={24} color={optionsMenuDisabled ? '#AEAEAE' : undefined} />
                </button>
              ) : (variant === 'actionsMenu' || actions || showOptionsMenu) ? (
                <MOptionsMenu
                  options={dropdownOptions || []}
                  disabled={!!optionsMenuDisabled}
                />
              ) : null}
            </div>
          )}
        </div>
        {secondaryLabel && (
          <div className="body-xs-medium">{secondaryLabel}</div>
        )}
        {highlightedLabel && (
          <div className={styles.highlightedLabel}>{highlightedLabel}</div>
        )}
        {warningLabel && (
          <div className={styles.warningRow}>
            <IconAlertTriangle size={20} className={styles.warningIcon} />
            <span className={styles.warningText}>{warningLabel}</span>
          </div>
        )}
        {showEmphasizedTertiary && tertiaryLabel && emphasizedLabel ? (
          <div className={styles.tertiaryRow}>
            <span className="body-xs-medium">{tertiaryLabel}</span>
            <span className="body-xs-bold">{emphasizedLabel}</span>
          </div>
        ) : tertiaryLabel ? (
          <div className="body-xs-semibold colour-text-caption-n400">{tertiaryLabel}</div>
        ) : null}
      </div>
      <div className={`${styles.rightAction} ${centeredRightAction ? styles.centered : ''}`}>
        {variant === 'checkbox' && (
          <div
            className={checked ? styles.checkIcon : styles.uncheckIcon}
            role="checkbox"
            aria-checked={!!checked}
            tabIndex={0}
            onClick={() => onCheck?.(!checked)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') onCheck?.(!checked);
            }}
          >
            {checked && <IconCheck size={20} />}
          </div>
        )}
        {variant === 'radio' && (
          <MRadio checked={selected} onChange={e => onSelect?.(e.target.checked)} />
        )}
        {variant === 'custom' && rightActionComponent}
      </div>
      {showCTA && (
        <button className={styles.ctaButton} onClick={onAction}>{ctaLabel || 'Go'}</button>
      )}
    </div>
  );
};

export default MInfoCard;
